import { Icon } from '@iconify/react';
import type { Location } from '@up2stay/up2promo-api-client';
import { ExternalLinkType } from '@up2stay/up2promo-api-client';
import { toExternalHref } from '@up2stay/up2promo-commons';
import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
} from '@up2stay/up2promo-ui';
import { ChevronDown, ExternalLink, Plus, Trash2 } from 'lucide-react';

import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useIsMobile } from '@/core/hooks/use-mobile';

import type { LocationDetailDict } from './location-detail-types';
import type { LocationLinkEntry } from './location-link-entry.types';

/** All external link types supported in the UI (from server enum). */
const EXTERNAL_LINK_TYPES: ExternalLinkType[] = [
  ExternalLinkType.MENU,
  ExternalLinkType.INSTAGRAM,
  ExternalLinkType.FACEBOOK,
  ExternalLinkType.TIKTOK,
];

type LinkIcon = 'menu' | 'instagram' | 'facebook' | 'tiktok';

const ADD_LINK_OPTIONS: ReadonlyArray<{
  type: ExternalLinkType;
  getLabel: (d: LocationDetailDict['externalServices']) => string;
  icon: LinkIcon;
}> = [
  {
    type: ExternalLinkType.MENU,
    getLabel: (d) => d?.addLinkMenu ?? d?.menuUrlLabel ?? 'Menu URL',
    icon: 'menu',
  },
  {
    type: ExternalLinkType.INSTAGRAM,
    getLabel: (d) => d?.addLinkInstagram ?? d?.instagramLabel ?? 'Instagram',
    icon: 'instagram',
  },
  {
    type: ExternalLinkType.FACEBOOK,
    getLabel: (d) => d?.addLinkFacebook ?? d?.facebookLabel ?? 'Facebook page',
    icon: 'facebook',
  },
  {
    type: ExternalLinkType.TIKTOK,
    getLabel: (d) => d?.addLinkTiktok ?? d?.tiktokLabel ?? 'TikTok',
    icon: 'tiktok',
  },
];

function getLabelAndPlaceholder(
  type: ExternalLinkType,
  d: LocationDetailDict['externalServices'],
): { label: string; placeholder: string; isUrl: boolean } {
  switch (type) {
    case ExternalLinkType.MENU:
      return {
        label: d?.menuUrlLabel ?? 'Menu URL',
        placeholder: d?.menuUrlPlaceholder ?? 'https://example.com/menu',
        isUrl: true,
      };
    case ExternalLinkType.FACEBOOK:
      return {
        label: d?.facebookLabel ?? 'Facebook page',
        placeholder: d?.facebookPlaceholder ?? 'https://facebook.com/yourpage',
        isUrl: true,
      };
    case ExternalLinkType.TIKTOK:
      return {
        label: d?.tiktokLabel ?? 'TikTok',
        placeholder: d?.tiktokPlaceholder ?? '@username',
        isUrl: false,
      };
    case ExternalLinkType.INSTAGRAM:
    default:
      return {
        label: d?.instagramLabel ?? 'Instagram',
        placeholder: d?.instagramPlaceholder ?? '@username',
        isUrl: false,
      };
  }
}

export function isValidMenuUrl(value: string): boolean {
  const normalized = toExternalHref(value);
  if (!normalized) return true;
  try {
    const u = new URL(normalized);
    return (
      (u.protocol === 'http:' || u.protocol === 'https:') &&
      !!u.hostname &&
      u.hostname.includes('.')
    );
  } catch {
    return false;
  }
}

export function isValidInstagramHandle(value: string): boolean {
  const trimmed = value.trim().replace(/^@/, '');
  if (!trimmed) return true;
  return /^[\w.]+$/.test(trimmed) && trimmed.length <= 100;
}

export const isValidFacebookUrl = isValidMenuUrl;
export const isValidTiktokHandle = isValidInstagramHandle;

function LinkTypeIcon({ icon }: { icon: LinkIcon }) {
  if (icon === 'menu') {
    return <ExternalLink className="h-5 w-5 shrink-0 text-muted-foreground" />;
  }
  const iconName =
    icon === 'instagram'
      ? 'simple-icons:instagram'
      : icon === 'facebook'
        ? 'simple-icons:facebook'
        : 'simple-icons:tiktok';
  return (
    <Icon icon={iconName} className="h-5 w-5 shrink-0 text-muted-foreground" />
  );
}

function getAddOptionIcon(type: ExternalLinkType): LinkIcon {
  const opt = ADD_LINK_OPTIONS.find((o) => o.type === type);
  return opt?.icon ?? 'menu';
}

export interface LocationDetailExternalServicesTabProps {
  location: Location;
  linkEntries: LocationLinkEntry[];
  onLinkEntriesChange: (
    entries:
      | LocationLinkEntry[]
      | ((prev: LocationLinkEntry[]) => LocationLinkEntry[]),
  ) => void;
  addLinkOpen: boolean;
  onAddLinkOpenChange: (open: boolean) => void;
  menuUrlError: string | null;
  onMenuUrlErrorChange: (err: string | null) => void;
  instagramError: string | null;
  onInstagramErrorChange: (err: string | null) => void;
  facebookError: string | null;
  onFacebookErrorChange: (err: string | null) => void;
  tiktokError: string | null;
  onTiktokErrorChange: (err: string | null) => void;
  dict?: LocationDetailDict;
}

export function LocationDetailExternalServicesTab({
  location: _location,
  linkEntries,
  onLinkEntriesChange,
  addLinkOpen,
  onAddLinkOpenChange,
  menuUrlError,
  onMenuUrlErrorChange,
  instagramError,
  onInstagramErrorChange,
  facebookError,
  onFacebookErrorChange,
  tiktokError,
  onTiktokErrorChange,
  dict,
}: LocationDetailExternalServicesTabProps) {
  const d = dict?.externalServices;
  const isMobile = useIsMobile();
  const sectionTitle = d?.title ?? 'Links';
  const sectionFooter = d?.description;

  const getErrorForType = (type: ExternalLinkType): string | null => {
    switch (type) {
      case ExternalLinkType.MENU:
        return menuUrlError;
      case ExternalLinkType.INSTAGRAM:
        return instagramError;
      case ExternalLinkType.FACEBOOK:
        return facebookError;
      case ExternalLinkType.TIKTOK:
        return tiktokError;
      default:
        return null;
    }
  };

  const clearErrorForType = (type: ExternalLinkType) => {
    switch (type) {
      case ExternalLinkType.MENU:
        onMenuUrlErrorChange(null);
        break;
      case ExternalLinkType.INSTAGRAM:
        onInstagramErrorChange(null);
        break;
      case ExternalLinkType.FACEBOOK:
        onFacebookErrorChange(null);
        break;
      case ExternalLinkType.TIKTOK:
        onTiktokErrorChange(null);
        break;
    }
  };

  const validateOnBlur = (type: ExternalLinkType, value: string) => {
    const v = value.trim();
    switch (type) {
      case ExternalLinkType.MENU:
        if (v && !isValidMenuUrl(value)) {
          onMenuUrlErrorChange(
            d?.menuUrlInvalid ?? 'Please enter a valid URL (e.g. https://…)',
          );
        } else onMenuUrlErrorChange(null);
        break;
      case ExternalLinkType.FACEBOOK:
        if (v && !isValidFacebookUrl(value)) {
          onFacebookErrorChange(
            d?.facebookInvalid ?? 'Please enter a valid URL (e.g. https://…)',
          );
        } else onFacebookErrorChange(null);
        break;
      case ExternalLinkType.INSTAGRAM:
        if (v && !isValidInstagramHandle(value)) {
          onInstagramErrorChange(
            d?.instagramInvalid ??
              'Use only letters, numbers, dots and underscores',
          );
        } else onInstagramErrorChange(null);
        break;
      case ExternalLinkType.TIKTOK:
        if (v && !isValidTiktokHandle(value)) {
          onTiktokErrorChange(
            d?.tiktokInvalid ??
              'Use only letters, numbers, dots and underscores',
          );
        } else onTiktokErrorChange(null);
        break;
    }
  };

  const stripHandlePrefix = (type: ExternalLinkType, v: string) =>
    type === ExternalLinkType.INSTAGRAM || type === ExternalLinkType.TIKTOK
      ? v.trim().replace(/^@/, '')
      : v;

  const canAddMore = EXTERNAL_LINK_TYPES.some(
    (t) => !linkEntries.some((e) => e.type === t),
  );

  return (
    <div className="flex min-w-0 w-full flex-col gap-6">
      <SettingsSection
        id="location-external-links"
        title={sectionTitle}
        footer={sectionFooter}
        grouped
      >
        <SettingsGroupRows>
          {linkEntries.map((entry, index) => {
            const { label, placeholder, isUrl } = getLabelAndPlaceholder(
              entry.type,
              d,
            );
            const err = getErrorForType(entry.type);
            const inputId = `location-link-${entry.type}-${index}`;
            return (
              <SettingsGroupRow
                key={`${entry.type}-${index}`}
                separated={index > 0}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <LinkTypeIcon icon={getAddOptionIcon(entry.type)} />
                      <span className="truncate text-[13.5px] font-medium leading-relaxed text-foreground">
                        {label}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 opacity-70 hover:opacity-100"
                      onClick={() => {
                        onLinkEntriesChange((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                        clearErrorForType(entry.type);
                      }}
                      aria-label={d?.removeLink ?? 'Remove link'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input
                    id={inputId}
                    type="text"
                    inputMode={isUrl ? 'url' : 'text'}
                    autoComplete={isUrl ? 'url' : 'username'}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={entry.value}
                    onChange={(e) => {
                      const valueToStore = stripHandlePrefix(
                        entry.type,
                        e.target.value,
                      );
                      onLinkEntriesChange((prev) =>
                        prev.map((ent, i) =>
                          i === index ? { ...ent, value: valueToStore } : ent,
                        ),
                      );
                      if (getErrorForType(entry.type)) {
                        clearErrorForType(entry.type);
                      }
                    }}
                    onBlur={() => validateOnBlur(entry.type, entry.value)}
                    placeholder={placeholder}
                    className={cn(
                      'w-full min-w-0',
                      err && 'border-destructive',
                    )}
                    aria-label={label}
                    aria-invalid={!!err}
                  />
                  {err ? (
                    <p className="text-[13.5px] leading-relaxed text-destructive">
                      {err}
                    </p>
                  ) : null}
                </div>
              </SettingsGroupRow>
            );
          })}

          {canAddMore ? (
            <SettingsGroupRow separated={linkEntries.length > 0}>
              <Collapsible
                open={addLinkOpen}
                onOpenChange={onAddLinkOpenChange}
                className="flex flex-col gap-2"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    aria-label={d?.addLink ?? 'Add link'}
                    className={cn(
                      'group flex w-full items-center justify-center gap-2 rounded-[var(--r-control)] border-2 border-dashed border-[color:var(--ghost-accent-border)] px-4',
                      isMobile ? 'py-4' : 'py-3',
                      'text-[color:var(--ghost-accent-fg)] transition-colors hover:bg-[color:var(--ghost-accent-bg)]',
                    )}
                  >
                    <Plus className="h-5 w-5" />
                    <span className="font-medium">
                      {d?.addLink ?? 'Add link'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        addLinkOpen && 'rotate-180',
                      )}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="flex flex-col gap-1 pt-1">
                    {ADD_LINK_OPTIONS.filter(
                      (opt) => !linkEntries.some((e) => e.type === opt.type),
                    ).map((opt) => (
                      <button
                        key={opt.type}
                        type="button"
                        onClick={() => {
                          onLinkEntriesChange((prev) => [
                            ...prev,
                            { type: opt.type, value: '' },
                          ]);
                          onAddLinkOpenChange(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-[var(--r-control)] px-2 py-2.5 text-left transition-colors hover:bg-[color:var(--ghost-accent-bg)]"
                      >
                        <LinkTypeIcon icon={opt.icon} />
                        <span className="text-[13.5px] font-medium leading-relaxed">
                          {opt.getLabel(d)}
                        </span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </SettingsGroupRow>
          ) : null}
        </SettingsGroupRows>
      </SettingsSection>
    </div>
  );
}
